/**
 * Product page: choice (size or flavour) and pack selection, gallery, add to cart and sticky bar.
 * No static imports: the module must run even if the theme's event helpers change.
 */
class MyoTapeProduct extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    const data = this.querySelector('[data-mt-variants]');
    if (!data) return;

    this.variants = JSON.parse(data.textContent);
    this.moneyFormat = (this.dataset.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
    this.form = this.querySelector('.mt-form');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.planInput = this.form?.querySelector('input[name="selling_plan"]');

    const current = this.variants.find((v) => String(v.id) === String(this.idInput?.value)) || this.variants[0];
    this.state = { size: current.size, nights: current.nights, plan: '' };

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('change', this.onChange, { signal });
    this.form?.addEventListener('submit', this.onSubmit, { signal });
    this.querySelector('[data-mt-sticky-add]')?.addEventListener('click', () => this.form?.requestSubmit(), { signal });

    this.querySelectorAll('[data-mt-open-size]').forEach((btn) =>
      btn.addEventListener('click', () => this.querySelector('[data-mt-size-dialog]')?.showModal(), { signal })
    );
    this.querySelectorAll('[data-mt-close-size]').forEach((btn) =>
      btn.addEventListener('click', () => btn.closest('dialog')?.close(), { signal })
    );
    this.querySelector('[data-mt-size-dialog]')?.addEventListener(
      'click',
      (event) => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      },
      { signal }
    );

    this.setupGallery(signal);
    this.setupSticky();
    this.update();
    this.goToMedia(current.media, 'instant');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.ready = false;
  }

  /** @param {Event} event */
  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.dataset.mtRole === 'size') this.state.size = input.value;
    else if (input.dataset.mtRole === 'pack') this.state.nights = Number(input.value);
    else if (input.dataset.mtRole === 'plan') this.state.plan = input.value;
    else return;

    this.update();
  };

  sizeVariants() {
    return this.variants.filter((v) => this.state.size == null || v.size === this.state.size);
  }

  resolve() {
    const list = this.sizeVariants();
    return list.find((v) => v.nights === this.state.nights) || list.find((v) => v.available) || list[0];
  }

  /** Savings are shown only against a real compare at price. */
  reference(variant) {
    return variant.compare > variant.price ? variant.compare : variant.price;
  }

  update() {
    const variant = this.resolve();
    if (!variant) return;
    this.state.nights = variant.nights;
    const list = this.sizeVariants();
    const subscribed = Boolean(this.state.plan) && variant.planPrice != null;
    const pay = subscribed ? variant.planPrice : variant.price;

    if (this.idInput) this.idInput.value = variant.id;
    if (this.planInput) {
      this.planInput.value = subscribed ? this.state.plan : '';
      this.planInput.disabled = !subscribed;
    }

    this.querySelectorAll('[data-mt-role="size"]').forEach((input) => {
      input.checked = input.value === variant.size;
    });

    this.querySelectorAll('[data-mt-hint-for]').forEach((el) => {
      el.hidden = el.dataset.mtHintFor !== variant.size;
    });

    this.querySelectorAll('[data-mt-pack]').forEach((card) => {
      const nights = Number(card.dataset.mtPack);
      const match = list.find((v) => v.nights === nights);
      const input = card.querySelector('input');
      card.hidden = !match;
      if (!match || !input) return;

      const ref = this.reference(match);
      const save = ref > match.price ? Math.floor(((ref - match.price) * 100) / ref) : 0;
      input.checked = match.id === variant.id;
      card.classList.toggle('is-active', match.id === variant.id);
      card.classList.toggle('is-soldout', !match.available);
      this.text(card, '[data-mt-price]', this.money(match.price));
      this.text(card, '[data-mt-unit]', this.money(Math.round(match.price / nights)));
      this.text(card, '[data-mt-compare]', save > 0 ? this.money(ref) : '');
      const saveEl = card.querySelector('[data-mt-save]');
      if (saveEl) {
        saveEl.hidden = save <= 0;
        saveEl.textContent = (saveEl.dataset.template || 'Save [pct]%').replace('[pct]', String(save));
      }
    });

    this.querySelectorAll('[data-mt-plan-price]').forEach((el) => {
      const value = el.dataset.mtPlanPrice === 'plan' ? variant.planPrice ?? variant.price : variant.price;
      el.textContent = this.money(value);
    });

    const ref = this.reference(variant);
    this.querySelectorAll('[data-mt-main-compare]').forEach((el) => {
      el.textContent = ref > variant.price ? this.money(ref) : '';
    });

    const ctaPrice = this.money(pay);
    this.querySelectorAll('[data-mt-cta-price]').forEach((el) => (el.textContent = ctaPrice));
    this.querySelectorAll('[data-mt-sticky-label]').forEach((el) => {
      el.textContent = [variant.size, variant.nights ? `${variant.nights} nights` : ''].filter(Boolean).join(', ');
    });

    this.querySelectorAll('[data-mt-add], [data-mt-sticky-add]').forEach((btn) => {
      btn.disabled = !variant.available;
      btn.classList.toggle('is-soldout', !variant.available);
    });

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore */
    }

    this.goToMedia(variant.media, 'smooth');
  }

  text(root, selector, value) {
    const el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  /** @param {number} cents */
  money(cents) {
    const value = (cents || 0) / 100;
    const group = (fixed, thousands, decimal) => {
      const [whole, fraction] = fixed.split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value.toFixed(0), ',', '.');
        case 'amount_with_comma_separator':
          return group(value.toFixed(2), '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value.toFixed(0), '.', ',');
        case 'amount_with_space_separator':
          return group(value.toFixed(2), ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(value.toFixed(2), "'", '.');
        default:
          return group(value.toFixed(2), ',', '.');
      }
    });
  }

  /** Loads the theme's cart events if they exist. Never throws. */
  async events() {
    if (this.eventsModule !== undefined) return this.eventsModule;
    try {
      this.eventsModule = await import('@shopify/events');
    } catch (e) {
      this.eventsModule = null;
    }
    return this.eventsModule;
  }

  cartDrawer() {
    return document.querySelector('theme-drawer#cart-drawer');
  }

  /** @param {SubmitEvent} event */
  onSubmit = async (event) => {
    event.preventDefault();
    if (this.busy || !this.form) return;

    const buttons = this.querySelectorAll('[data-mt-add], [data-mt-sticky-add]');
    const error = this.querySelector('[data-mt-error]');
    const status = this.querySelector('[data-mt-status]');
    this.busy = true;
    buttons.forEach((btn) => btn.classList.add('is-loading'));
    if (error) error.hidden = true;

    const body = new FormData(this.form);
    const variantId = String(body.get('id'));
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const events = await this.events();
    const Evt = events?.CartLinesUpdateEvent;
    let deferred = null;
    if (Evt && typeof Evt.createPromise === 'function') {
      deferred = Evt.createPromise();
      try {
        this.dispatchEvent(
          new Evt({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
      } catch (e) {
        deferred = null;
      }
    }

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body,
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Something went wrong. Please try again.');
      }

      let cart = null;
      try {
        const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (e) {
        cart = null;
      }

      if (deferred) {
        deferred.resolve({
          cart: cart && typeof Evt.createCartFromAjaxResponse === 'function' ? Evt.createCartFromAjaxResponse(cart) : undefined,
          detail: {
            items: cart?.items,
            source: 'product-form-component',
            sourceId: this.id || 'myotape-product',
            itemCount: 1,
            productId: this.dataset.productId,
            sections: result.sections,
            didError: false,
          },
        });
      }

      buttons.forEach((btn) => btn.classList.add('is-added'));
      setTimeout(() => buttons.forEach((btn) => btn.classList.remove('is-added')), 2000);
      if (status) status.textContent = this.dataset.addedText || 'Added to cart';

      const drawer = this.cartDrawer();
      if (drawer) {
        const autoOpens = Boolean(drawer.querySelector('cart-drawer-component[auto-open]')) && deferred;
        if (!autoOpens) {
          if (typeof drawer.open === 'function') drawer.open();
          else drawer.setAttribute('open', '');
        }
      } else {
        window.location.href = window.Theme?.routes?.cart_url || '/cart';
      }
    } catch (err) {
      if (deferred) deferred.reject(err);
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.hidden = false;
      }
    } finally {
      this.busy = false;
      buttons.forEach((btn) => btn.classList.remove('is-loading'));
    }
  };

  /** @param {AbortSignal} signal */
  setupGallery(signal) {
    this.track = this.querySelector('[data-mt-track]');
    if (!this.track) return;
    const slides = [...this.track.querySelectorAll('[data-mt-slide]')];
    const thumbs = [...this.querySelectorAll('[data-mt-thumb]')];

    const setActive = (index) => {
      thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      thumbs[index]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      this.activeSlide = index;
    };

    const goTo = (index, behavior = 'smooth') => {
      const slide = slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior });
      setActive(index);
    };
    this.goToIndex = goTo;

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i), { signal }));
    this.querySelector('[data-mt-prev]')?.addEventListener('click', () => goTo(Math.max(0, (this.activeSlide || 0) - 1)), { signal });
    this.querySelector('[data-mt-next]')?.addEventListener('click', () => goTo(Math.min(slides.length - 1, (this.activeSlide || 0) + 1)), { signal });

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          setActive(Math.min(slides.length - 1, Math.max(0, index)));
        });
      },
      { signal, passive: true }
    );
    setActive(0);
  }

  goToMedia(mediaId, behavior) {
    if (!mediaId || !this.track || !this.goToIndex) return;
    const slides = [...this.track.querySelectorAll('[data-mt-slide]')];
    const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.goToIndex(index, reduce ? 'instant' : behavior);
  }

  setupSticky() {
    const bar = this.querySelector('[data-mt-sticky]');
    const cta = this.querySelector('[data-mt-add]');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      bar.setAttribute('aria-hidden', String(!past));
      bar.querySelectorAll('button').forEach((b) => (b.tabIndex = past ? 0 : -1));
    });
    this.observer.observe(cta);
  }
}

if (!customElements.get('myotape-product')) customElements.define('myotape-product', MyoTapeProduct);
